import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, Mail, Send, CheckCircle, Code, Gift } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEmailSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailTemplate, setEmailTemplate] = useState("");
  const [giftType, setGiftType] = useState("coupon");
  const [giftCouponCode, setGiftCouponCode] = useState("");
  const [giftPdfUrl, setGiftPdfUrl] = useState("");

  useEffect(() => {
    fetchEmailSettings();
  }, []);

  const fetchEmailSettings = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/email-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      setIsActive(data.is_active || false);
      setApiKey(data.api_key || "");
      setSenderEmail(data.sender_email || "");
      setEmailSubject(data.email_subject || "Hoşgeldiniz - Alcodot");
      setEmailTemplate(data.email_template || "");
      setGiftType(data.gift_type || "coupon");
      setGiftCouponCode(data.gift_coupon_code || "");
      setGiftPdfUrl(data.gift_pdf_url || "");
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem("admin_token");

    if (isActive && (!apiKey.trim() || !senderEmail.trim())) {
      toast.error("API Key ve Gönderici Email gerekli");
      return;
    }

    setSaving(true);

    try {
      await axios.put(
        `${API}/admin/email-settings`,
        {
          is_active: isActive,
          api_key: apiKey,
          sender_email: senderEmail,
          email_subject: emailSubject,
          email_template: emailTemplate,
          gift_type: giftType,
          gift_coupon_code: giftCouponCode || null,
          gift_pdf_url: giftPdfUrl || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Email ayarları güncellendi!");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Güncelleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail.trim() || !testEmail.includes("@")) {
      toast.error("Geçerli bir email adresi girin");
      return;
    }

    const token = localStorage.getItem("admin_token");
    setTesting(true);

    try {
      await axios.post(
        `${API}/admin/test-email`,
        { test_email: testEmail },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Test emaili gönderildi! Gelen kutunuzu kontrol edin.");
    } catch (error) {
      console.error("Error:", error);
      toast.error(
        error.response?.data?.detail || "Test emaili gönderilemedi"
      );
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="text-teal-400">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-email-settings">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-black text-gradient">Email Ayarları</h2>
        <div className="flex items-center gap-3">
          <Label className="text-teal-400 font-semibold">Email Gönderimi Aktif</Label>
          <Switch
            checked={isActive}
            onCheckedChange={setIsActive}
            className="data-[state=checked]:bg-teal-500"
            data-testid="email-active-switch"
          />
        </div>
      </div>

      <div className="glass-card-premium rounded-2xl p-8 neon-border space-y-6">
        {/* Info */}
        <div className="bg-blue-500/10 border-2 border-blue-500/30 rounded-2xl p-5">
          <p className="text-blue-400 text-sm font-semibold mb-2">
            <Mail className="w-4 h-4 inline mr-2" />
            SendGrid API Key Nasıl Alınır?
          </p>
          <p className="text-gray-300 text-xs leading-relaxed">
            1. SendGrid.com'da hesap oluşturun<br />
            2. Settings → API Keys → Create API Key<br />
            3. "Full Access" seçin ve oluşturun<br />
            4. API Key'i kopyalayıp aşağıya yapıştırın
          </p>
        </div>

        {/* SendGrid API Key */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            SendGrid API Key *
          </Label>
          <Input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="SG.xxxxxxxxxxxx"
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg font-mono"
            data-testid="api-key-input"
          />
        </div>

        {/* Sender Email */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            Gönderici Email *
          </Label>
          <Input
            type="email"
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            placeholder="noreply@alcodot.com"
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg"
            data-testid="sender-email-input"
          />
          <p className="text-xs text-gray-500">
            ⚠️ Bu email adresi SendGrid'de doğrulanmış olmalıdır
          </p>
        </div>

        {/* Email Subject */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            Email Konusu
          </Label>
          <Input
            type="text"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Hoşgeldiniz - Alcodot"
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg"
            data-testid="email-subject-input"
          />
        </div>

        {/* Gift Settings */}
        <div className="space-y-4 border-t-2 border-teal-500/20 pt-6">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            <Gift className="w-4 h-4 inline mr-2" />
            Hediye Ayarları
          </Label>

          {/* Gift Type */}
          <div className="space-y-3">
            <Label className="text-xs text-gray-400">Hediye Tipi</Label>
            <Select value={giftType} onValueChange={setGiftType}>
              <SelectTrigger className="h-12 rounded-xl bg-gray-900/50 border-2 border-teal-500/30 text-white" data-testid="gift-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-teal-500/30">
                <SelectItem value="coupon" className="text-white hover:bg-teal-500/20">
                  İndirim Kuponu
                </SelectItem>
                <SelectItem value="pdf" className="text-white hover:bg-teal-500/20">
                  PDF Dosyası (E-Book)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Coupon Code */}
          {giftType === "coupon" && (
            <div className="space-y-3">
              <Label className="text-xs text-gray-400">Kupon Kodu</Label>
              <Input
                value={giftCouponCode}
                onChange={(e) => setGiftCouponCode(e.target.value)}
                placeholder="örn: ALCODOT15"
                className="h-12 rounded-xl bg-gray-900/50 border-2 border-teal-500/30 text-white font-mono"
                data-testid="coupon-code-input"
              />
              <p className="text-xs text-gray-500">Bu kupon kodu email'de gösterilecek</p>
            </div>
          )}

          {/* PDF URL */}
          {giftType === "pdf" && (
            <div className="space-y-3">
              <Label className="text-xs text-gray-400">PDF URL</Label>
              <Input
                type="url"
                value={giftPdfUrl}
                onChange={(e) => setGiftPdfUrl(e.target.value)}
                placeholder="https://cdn.alcodot.com/guvenli-surus-rehberi.pdf"
                className="h-12 rounded-xl bg-gray-900/50 border-2 border-teal-500/30 text-white"
                data-testid="pdf-url-input"
              />
              <p className="text-xs text-gray-500">
                PDF dosyanızı bir hosting'e yükleyin ve URL'sini buraya girin
              </p>
            </div>
          )}
        </div>

        {/* Email Template */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            <Code className="w-4 h-4 inline mr-2" />
            Email İçeriği (HTML)
          </Label>
          <Textarea
            value={emailTemplate}
            onChange={(e) => setEmailTemplate(e.target.value)}
            placeholder="Email içeriğinizi buraya yazın..."
            rows={12}
            className="rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white font-mono text-sm"
            data-testid="email-template-input"
          />
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <p className="text-blue-400 text-xs font-bold mb-2">Kullanılabilir Değişkenler:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs font-mono">
              <code className="text-teal-400">{'{{name}}'}</code>
              <code className="text-teal-400">{'{{email}}'}</code>
              <code className="text-teal-400">{'{{phone}}'}</code>
              <code className="text-teal-400">{'{{discount_text}}'}</code>
              <code className="text-teal-400">{'{{gift_section}}'}</code>
              <code className="text-teal-400">{'{{coupon_code}}'}</code>
            </div>
            <p className="text-gray-400 text-xs mt-2">
              Bu değişkenler otomatik olarak kullanıcı bilgileriyle değiştirilir. gift_section hediyeyi otomatik ekler.
            </p>
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-14 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 text-black font-black text-lg shadow-lg neon-glow"
          data-testid="save-email-btn"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? "Kaydediliyor..." : "Email Ayarlarını Kaydet"}
        </Button>

        {/* Test Email Section */}
        {isActive && apiKey && senderEmail && (
          <div className="border-t-2 border-teal-500/20 pt-6 mt-6">
            <h3 className="text-xl font-black text-white mb-4">Test Email Gönder</h3>
            <div className="flex gap-3">
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@email.com"
                className="h-12 rounded-xl bg-gray-900/50 border-2 border-teal-500/30 text-white"
                data-testid="test-email-input"
              />
              <Button
                onClick={handleTestEmail}
                disabled={testing}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-6 font-bold"
                data-testid="send-test-email-btn"
              >
                {testing ? (
                  <>
                    <Send className="w-4 h-4 mr-2 animate-spin" />
                    Gönderiliyor
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Test Gönder
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailSettings;